import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link, useNavigate } from 'react-router-dom';

// Import components
import ProjectLayout from '../components/layout/ProjectLayout';
import { scrollToSection } from '../utils/scrollUtils';
import { fetchTopPicksData, checkForUpdates, StockAnalysis, TopPickSimplified, TopPicksData } from '../services/topPickService';

// Type definitions
interface ScoreBarProps {
  scoreValue: number;
}

interface StockChangeProps {
  positive: boolean;
}

interface ButtonProps {
  primary?: boolean;
}

interface SystemNodeProps {
  id: 'server' | 'api' | 'storage' | 'web';
}

// Define a new interface for styling signal values
interface SignalProps {
  signal: string;
}

// Component implementation
const StockAnalysisPage = () => {
  const [scrollOpacity, setScrollOpacity] = useState(1);
  const navigate = useNavigate();
  const [lastUpdated, setLastUpdated] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [selectedMarket, setSelectedMarket] = useState<'US' | 'Indian'>('US');
  const [usMarkets, setUsMarkets] = useState<TopPickSimplified[]>([]);
  const [indianMarkets, setIndianMarkets] = useState<TopPickSimplified[]>([]);
  const [showDataGuide, setShowDataGuide] = useState(false);
  
  // Fetch top bullish pick and top picks table
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching data from JSON file...'); // Debug log
        
        const fullData = await fetchTopPicksData();
        console.log('Fetched full data:', fullData); // Debug log
        
        if (!fullData) {
          throw new Error('No data received from JSON file');
        }
        
        setLastUpdated(fullData.last_updated);
        // Store both market data
        setUsMarkets(fullData.us_markets || []);
        setIndianMarkets(fullData.indian_markets || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    // Initial fetch
    fetchData();
    
    // Set up auto-refresh to check for updates every 30 seconds
    const interval = setInterval(async () => {
      try {
        const hasUpdates = await checkForUpdates();
        if (hasUpdates) {
          console.log('JSON file updated, refreshing data...');
          const fullData = await fetchTopPicksData();
          if (fullData) {
            setLastUpdated(fullData.last_updated);
            setUsMarkets(fullData.us_markets || []);
            setIndianMarkets(fullData.indian_markets || []);
          }
        }
      } catch (error) {
        console.error('Error checking for updates:', error);
      }
    }, 30000); // Check every 30 seconds

    return () => clearInterval(interval);
  }, []);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  // Handle scroll effect for scroll button
  useEffect(() => {
    const handleScroll = () => {
      const opacity = Math.max(1 - window.scrollY / 400, 0);
      setScrollOpacity(opacity);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Check if mobile device
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Check on mount and add listener for resize
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Animation refs for different sections - even lower threshold and larger margin for mobile
  const [aboutRef, aboutInView] = useInView({
    threshold: isMobile ? 0.05 : 0.2, // Lower threshold for mobile
    rootMargin: isMobile ? "100px" : "0px", // Increased margin for mobile
  });

  const [techStackRef, techStackInView] = useInView({
    threshold: isMobile ? 0.05 : 0.2,
    rootMargin: isMobile ? "100px" : "0px",
  });

  const [recRef, recInView] = useInView({
    threshold: isMobile ? 0.05 : 0.2,
    rootMargin: isMobile ? "100px" : "0px",
  });

  // Get current market data based on selection
  const getCurrentMarketData = () => {
    return selectedMarket === 'US' ? usMarkets : indianMarkets;
  };

  // Get top pick for selected market
  const getMarketTopPick = () => {
    const marketData = getCurrentMarketData();
    return marketData.length > 0 ? marketData[0] : null;
  };

  // Get table data (positions 2-10 for selected market)
  const getTableData = () => {
    const marketData = getCurrentMarketData();
    return marketData.slice(1); // Skip first one as it's shown as top pick
  };

  // Get currency symbol based on market
  const getCurrencySymbol = () => {
    return selectedMarket === 'US' ? '$' : '₹';
  };

  console.log('StockAnalysisPage rendering, loading:', loading); // Debug log

  return (
    <ProjectLayout transparentHeader={true}>
      <HeroSection>
        <HeroContent>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1>AI Stock Analysis Platform</h1>
            <p>Combining financial expertise with AI-driven insights</p>
          </motion.div>
        </HeroContent>
        
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: scrollOpacity }}
          transition={{ duration: 0.3 }}
        >
          <ScrollDown onClick={() => scrollToSection('recommendations')}>
            <span>Scroll Down</span>
            <svg 
              width="16" 
              height="24" 
              viewBox="0 0 16 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
            >
              <path 
                d="M8 0L8 20M8 20L1 13M8 20L15 13" 
                stroke="currentColor" 
                strokeWidth="2"
              />
            </svg>
          </ScrollDown>
        </motion.div>
      </HeroSection>

      <PageContent>
        <Section 
          id="recommendations"
          ref={recRef}
          as={motion.section}
          initial={{ opacity: 0, y: 50 }}
          animate={recInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>Stock Recommendations</SectionTitle>
          
          <MarketSelectorContainer>
            <MarketSelector>
              <MarketOption 
                active={selectedMarket === 'US'} 
                onClick={() => setSelectedMarket('US')}
              >
                US Markets
              </MarketOption>
              <MarketOption 
                active={selectedMarket === 'Indian'} 
                onClick={() => setSelectedMarket('Indian')}
              >
                Indian Markets
              </MarketOption>
            </MarketSelector>
          </MarketSelectorContainer>
          
          <RecommendationHeader>
            <h3>AI Daily Stock Rankings</h3>
            <span>Updated daily</span>
          </RecommendationHeader>
          
          <TopPickSection>
              {loading ? (
                <LoadingText>Loading latest recommendation...</LoadingText>
              ) : getMarketTopPick() ? (
                <TopPickContainer
                  as={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <TopPickMeta>
                    <LastUpdated>
                      Last updated: {lastUpdated ? new Date(lastUpdated).toLocaleString('en-US', { timeZone: 'Asia/Kolkata' }) : 'Loading...'}
                    </LastUpdated>
                    <TopPickMetaRight>
                      <BullishScoreIndicator score={getMarketTopPick()?.score || 0}>
                        {getMarketTopPick()?.score?.toFixed(1) || 0}% Bullish
                      </BullishScoreIndicator>
                    </TopPickMetaRight>
                  </TopPickMeta>

                  <StockInfo>
                    <StockTitleRow>
                      <StockSymbol>{getMarketTopPick()?.ticker || ''}</StockSymbol>
                      <RankIndicator>#1</RankIndicator>
                    </StockTitleRow>
                    <StockName>Top Pick - {selectedMarket} Market</StockName>
                  </StockInfo>

                  <StockMetrics>
                    <MetricItem>
                      <MetricLabel>Price</MetricLabel>
                      <MetricValue>{getCurrencySymbol()}{getMarketTopPick()?.price?.toFixed(2) || 'N/A'}</MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>Signal</MetricLabel>
                      <MetricValue>
                        <SignalValue signal={getMarketTopPick()?.signal || 'N/A'}>
                          {getMarketTopPick()?.signal || 'N/A'}
                        </SignalValue>
                      </MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>Tier</MetricLabel>
                      <MetricValue>
                        <TierValue tier={getMarketTopPick()?.tier || 'N/A'}>
                          {getMarketTopPick()?.tier || 'N/A'}
                        </TierValue>
                      </MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>Entry Zone</MetricLabel>
                      <MetricValue>{getMarketTopPick()?.entry || 'N/A'}</MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>Risk/Reward</MetricLabel>
                      <MetricValue>{getMarketTopPick()?.risk_reward || 'N/A'}</MetricValue>
                    </MetricItem>
                  </StockMetrics>
                  
                  <StockMetrics>
                    <MetricItem>
                      <MetricLabel>Weekly Change</MetricLabel>
                      <MetricValue change={getMarketTopPick()?.week_change || undefined}>
                        {getMarketTopPick()?.week_change !== null && getMarketTopPick()?.week_change !== undefined ? 
                          `${getMarketTopPick()!.week_change! > 0 ? '+' : ''}${getMarketTopPick()!.week_change!.toFixed(1)}%` : 
                          'N/A'
                        }
                      </MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>30-Day Change</MetricLabel>
                      <MetricValue change={getMarketTopPick()?.month_change || undefined}>
                        {getMarketTopPick()?.month_change !== null && getMarketTopPick()?.month_change !== undefined ? 
                          `${getMarketTopPick()!.month_change! > 0 ? '+' : ''}${getMarketTopPick()!.month_change!.toFixed(1)}%` : 
                          'N/A'
                        }
                      </MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>3D Momentum</MetricLabel>
                      <MetricValue change={getMarketTopPick()?.momentum_3d || 0}>
                        {getMarketTopPick()?.momentum_3d !== null ? 
                          `${getMarketTopPick()!.momentum_3d! > 0 ? '+' : ''}${getMarketTopPick()!.momentum_3d!.toFixed(1)}%` : 
                          'N/A'
                        }
                      </MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>Vol Ratio</MetricLabel>
                      <MetricValue>{getMarketTopPick()?.volume_ratio?.toFixed(1) || 'N/A'}</MetricValue>
                    </MetricItem>
                    <MetricItem>
                      <MetricLabel>Regime</MetricLabel>
                      <MetricValue>
                        <RegimeValue regime={getMarketTopPick()?.regime || 'N/A'}>
                          {getMarketTopPick()?.regime || 'N/A'}
                        </RegimeValue>
                      </MetricValue>
                    </MetricItem>
                  </StockMetrics>

                  {/* Additional detailed metrics */}
                  <TopPickDetails>
                    {/* New Fields */}
                    <DetailItem>
                      <DetailLabel>Score Tier:</DetailLabel>
                      <DetailValue>
                        <TierValue tier={getMarketTopPick()?.tier || 'N/A'}>
                          {getMarketTopPick()?.tier || 'N/A'}
                        </TierValue>
                      </DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Signal:</DetailLabel>
                      <DetailValue>
                        <SignalValue signal={getMarketTopPick()?.signal || 'N/A'}>
                          {getMarketTopPick()?.signal || 'N/A'}
                        </SignalValue>
                      </DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Entry Zone:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.entry || 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Risk/Reward:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.risk_reward || 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Volume Ratio:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.volume_ratio?.toFixed(1) || 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>3D Momentum:</DetailLabel>
                      <DetailValue>
                        <ChangeValue change={getMarketTopPick()?.momentum_3d || 0}>
                          {getMarketTopPick()?.momentum_3d !== null ? 
                            `${getMarketTopPick()!.momentum_3d! > 0 ? '+' : ''}${getMarketTopPick()!.momentum_3d!.toFixed(1)}%` : 
                            'N/A'
                          }
                        </ChangeValue>
                      </DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Market Regime:</DetailLabel>
                      <DetailValue>
                        <RegimeValue regime={getMarketTopPick()?.regime || 'N/A'}>
                          {getMarketTopPick()?.regime || 'N/A'}
                        </RegimeValue>
                      </DetailValue>
                    </DetailItem>
                    
                    {/* Existing Fields */}
                    <DetailItem>
                      <DetailLabel>Pattern Score:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.pattern_score || 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>ML Prediction:</DetailLabel>
                      <DetailValue>
                        <MacdValue status={getMarketTopPick()?.ml_prediction || 'N/A'}>
                          {getMarketTopPick()?.ml_prediction || 'N/A'}
                        </MacdValue>
                      </DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>ML Accuracy:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.ml_accuracy?.toFixed(1) || 'N/A'}%</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Sentiment Score:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.sentiment?.toFixed(2) || 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Support Level:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.support_level !== null && getMarketTopPick()?.support_level !== undefined ? `${getCurrencySymbol()}${getMarketTopPick()!.support_level!.toFixed(2)}` : 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Pivot Level:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.pivot_level !== null && getMarketTopPick()?.pivot_level !== undefined ? `${getCurrencySymbol()}${getMarketTopPick()!.pivot_level!.toFixed(2)}` : 'N/A'}</DetailValue>
                    </DetailItem>
                    
                    <DetailItem>
                      <DetailLabel>Resistance Level:</DetailLabel>
                      <DetailValue>{getMarketTopPick()?.resistance_level !== null && getMarketTopPick()?.resistance_level !== undefined ? `${getCurrencySymbol()}${getMarketTopPick()!.resistance_level!.toFixed(2)}` : 'N/A'}</DetailValue>
                    </DetailItem>
                  </TopPickDetails>

                  {selectedMarket === 'Indian' && (
                    <PrivateSystemNote>
                      <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                      >
                        Note: The data represents Nifty 100 stocks from the National Stock Exchange of India.
                      </motion.div>
                    </PrivateSystemNote>
                  )}

                  <AnalysisSection>
                    <AnalysisTitle>Market Analysis</AnalysisTitle>
                    <AnalysisText>
                      {selectedMarket === 'US' 
                        ? 'AI-powered analysis of US market trends and individual stock performance based on technical indicators, market sentiment, and machine learning predictions.'
                        : 'Comprehensive analysis of Indian market dynamics focusing on NSE-listed stocks with technical analysis and sentiment indicators adapted for the Indian market context.'
                      }
                    </AnalysisText>
                  </AnalysisSection>
                </TopPickContainer>
              ) : (
                <LoadingText>
                  Unable to load latest recommendations. Please check back later.
                  <br />
                  <small>The API server may be temporarily unavailable.</small>
                </LoadingText>
              )}
            </TopPickSection>

          {/* Top 10 Table - Positions 2-10 */}
          {!loading && getTableData().length > 0 && (
            <TopTenTableContainer
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
                <TopTenTable>
                  <thead>
                    <tr>
                      <th>Rank</th>
                      <th>Ticker</th>
                      <th>Score</th>
                      <th>Tier</th>
                      <th>Signal</th>
                      <th>Entry</th>
                      <th>R:R</th>
                      <th>Vol Ratio</th>
                      <th>Mom 3D</th>
                      <th>Regime</th>
                      <th>Price</th>
                      <th>Weekly %</th>
                      <th>Monthly %</th>
                      <th>Pattern</th>
                      <th>ML Pred</th>
                      <th>ML Acc%</th>
                      <th>Sentiment</th>
                      <th>RSI</th>
                      <th>MACD</th>
                      <th>Support</th>
                      <th>Pivot</th>
                      <th>Resistance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getTableData().map((stock, index) => (
                      <TopTenRow
                        key={stock.ticker}
                        as={motion.tr}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 * index }}
                      >
                        <td className="rank">#{index + 2}</td>
                        <td className="ticker">{stock.ticker}</td>
                        <td className="score">
                          <ScoreBadge score={stock.score}>
                            {stock.score.toFixed(1)}
                          </ScoreBadge>
                        </td>
                        <td className="tier">
                          <TierValue tier={stock.tier || 'N/A'}>
                            {stock.tier || 'N/A'}
                          </TierValue>
                        </td>
                        <td className="signal">
                          <SignalValue signal={stock.signal || 'N/A'}>
                            {stock.signal || 'N/A'}
                          </SignalValue>
                        </td>
                        <td className="entry">{stock.entry || 'N/A'}</td>
                        <td className="risk-reward">{stock.risk_reward || 'N/A'}</td>
                        <td className="vol-ratio">{stock.volume_ratio?.toFixed(1) || 'N/A'}</td>
                        <td className="momentum">
                          <ChangeValue change={stock.momentum_3d || 0}>
                            {stock.momentum_3d !== null ? 
                              `${stock.momentum_3d > 0 ? '+' : ''}${stock.momentum_3d.toFixed(1)}%` : 
                              'N/A'
                            }
                          </ChangeValue>
                        </td>
                        <td className="regime">
                          <RegimeValue regime={stock.regime || 'N/A'}>
                            {stock.regime || 'N/A'}
                          </RegimeValue>
                        </td>
                        <td className="price">{getCurrencySymbol()}{stock.price.toFixed(2)}</td>
                        <td className="change">
                          <ChangeValue change={stock.week_change || 0}>
                            {stock.week_change !== null ? 
                              `${stock.week_change > 0 ? '+' : ''}${stock.week_change.toFixed(1)}%` : 
                              'N/A'
                            }
                          </ChangeValue>
                        </td>
                        <td className="change">
                          <ChangeValue change={stock.month_change || 0}>
                            {stock.month_change !== null ? 
                              `${stock.month_change > 0 ? '+' : ''}${stock.month_change.toFixed(1)}%` : 
                              'N/A'
                            }
                          </ChangeValue>
                        </td>
                        <td className="pattern">{stock.pattern_score}</td>
                        <td className="ml-prediction">
                          <MacdValue status={stock.ml_prediction || 'N/A'}>
                            {stock.ml_prediction || 'N/A'}
                          </MacdValue>
                        </td>
                        <td className="ml-accuracy">{stock.ml_accuracy.toFixed(1)}%</td>
                        <td className="sentiment">
                          <SentimentValue sentiment={stock.sentiment}>
                            {stock.sentiment.toFixed(2)}
                          </SentimentValue>
                        </td>
                        <td className="rsi">
                          {stock.rsi !== null ? stock.rsi.toFixed(1) : 'N/A'}
                        </td>
                        <td className="macd">
                          <MacdValue status={stock.macd_status || 'N/A'}>
                            {stock.macd_status || 'N/A'}
                          </MacdValue>
                        </td>
                        <td className="support">
                          {stock.support_level !== null ? `${getCurrencySymbol()}${stock.support_level.toFixed(2)}` : 'N/A'}
                        </td>
                        <td className="pivot">
                          {stock.pivot_level !== null ? `${getCurrencySymbol()}${stock.pivot_level.toFixed(2)}` : 'N/A'}
                        </td>
                        <td className="resistance">
                          {stock.resistance_level !== null ? `${getCurrencySymbol()}${stock.resistance_level.toFixed(2)}` : 'N/A'}
                        </td>
                      </TopTenRow>
                    ))}
                  </tbody>
                </TopTenTable>
              </TopTenTableContainer>
          )}
            
          <DisclaimerText>
            * For demonstration purposes only. Not financial advice.
          </DisclaimerText>

          {/* Data Guide Section */}
          <DataGuideSection>
            <DataGuideToggle 
              onClick={() => setShowDataGuide(!showDataGuide)}
              expanded={showDataGuide}
            >
              <span>📊 Understanding the Data</span>
              <DataGuideChevron expanded={showDataGuide}>
                {showDataGuide ? '▲' : '▼'}
              </DataGuideChevron>
            </DataGuideToggle>
            
            {showDataGuide && (
              <DataGuideContent
                as={motion.div}
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <DataGuideGrid>
                  <DataGuideCategory>
                    <DataGuideCategoryTitle>📈 Basic Information</DataGuideCategoryTitle>
                    <DataGuideItem><strong>Rank:</strong> Position in bullish ranking (1 = most bullish)</DataGuideItem>
                    <DataGuideItem><strong>Ticker:</strong> Stock symbol (AAPL, HDFCBANK.NS)</DataGuideItem>
                    <DataGuideItem><strong>Score:</strong> Overall bullish potential (0-100%+)</DataGuideItem>
                    <DataGuideItem><strong>Tier:</strong> Stock quality tier (A/B/C/D)</DataGuideItem>
                    <DataGuideItem><strong>Price:</strong> Current price in USD ($) or Rupees (₹)</DataGuideItem>
                    <DataGuideCategoryTitle style={{ marginTop: '1.5rem' }}>📊 Performance</DataGuideCategoryTitle>
                    <DataGuideItem><strong>Week %:</strong> 5-day price change</DataGuideItem>
                    <DataGuideItem><strong>Month %:</strong> 22-day price change</DataGuideItem>
                    <DataGuideItem><strong>Mom 3D:</strong> 3-day momentum trend</DataGuideItem>
                  </DataGuideCategory>

                  <DataGuideCategory>
                    <DataGuideCategoryTitle>🔧 Technical Indicators</DataGuideCategoryTitle>
                    <DataGuideItem><strong>Pattern:</strong> Technical score /10 (higher = more bullish)</DataGuideItem>
                    <DataGuideItem><strong>RSI:</strong> 0-100 (&lt;30 oversold, &gt;70 overbought)</DataGuideItem>
                    <DataGuideItem><strong>MACD:</strong> Trend momentum (Bullish/Bearish)</DataGuideItem>
                    <DataGuideItem><strong>Vol Ratio:</strong> Current volume vs. average volume</DataGuideItem>
                    <DataGuideCategoryTitle style={{ marginTop: '1.5rem' }}>🤖 AI & Sentiment</DataGuideCategoryTitle>
                    <DataGuideItem><strong>ML Pred:</strong> Next-day AI prediction</DataGuideItem>
                    <DataGuideItem><strong>ML Acc:</strong> Model accuracy from backtesting</DataGuideItem>
                    <DataGuideItem><strong>Sentiment:</strong> News sentiment (-1 to +1)</DataGuideItem>
                  </DataGuideCategory>

                  <DataGuideCategory>
                    <DataGuideCategoryTitle>📍 Price Levels & Analysis</DataGuideCategoryTitle>
                    <DataGuideItem><strong>Signal:</strong> Trading direction (LONG/SHORT/MOMENTUM)</DataGuideItem>
                    <DataGuideItem><strong>Entry:</strong> Suggested price entry zone</DataGuideItem>
                    <DataGuideItem><strong>R:R:</strong> Risk to reward ratio</DataGuideItem>
                    <DataGuideItem><strong>Regime:</strong> Current market environment</DataGuideItem>
                    <DataGuideItem><strong>Support:</strong> Price floor where buying interest emerges</DataGuideItem>
                    <DataGuideItem><strong>Pivot:</strong> Technical reference point for analysis</DataGuideItem>
                    <DataGuideItem><strong>Resistance:</strong> Price ceiling where selling pressure increases</DataGuideItem>
                    <DataGuideItem style={{ marginTop: '1rem', fontSize: '0.9rem', fontStyle: 'italic' }}>
                      These levels help identify potential entry and exit points for trading decisions.
                    </DataGuideItem>
                  </DataGuideCategory>
                </DataGuideGrid>
                
                <DataGuideNote>
                  <strong>How Scoring Works:</strong> Score combines 40% technical patterns + 40% ML prediction + 20% sentiment.
                  Stocks are assigned quality tiers (A/B/C/D) based on overall scores and trading signals (LONG/SHORT/MOMENTUM) based on analysis.
                  US and Indian stocks are ranked separately within their respective markets.
                </DataGuideNote>
              </DataGuideContent>
            )}
          </DataGuideSection>

          <SystemFlowTitle>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                System Architecture
              </motion.div>
            </SystemFlowTitle>

          <SystemFlowContainer>
              {[
                { id: 'server', icon: '🏠', title: 'Home Server', desc: 'Real-time AI stock analysis runs 24/7 using Docker', delay: 0 },
                { id: 'api', icon: '🔑', title: 'Auth API', desc: 'Authentication and Authorization', delay: 0.2 },
                { id: 'storage', icon: '☁️', title: 'S3/CloudFront', desc: 'Cron job updates the latest stock data to S3 every day', delay: 0.4 },
                { id: 'web', icon: '🌐', title: 'Website', desc: 'Frontend interface built with React', delay: 0.6 }
              ].map((node, index, arr) => (
                <SystemNode
                  key={node.id}
                  id={node.id}
                  as={motion.div}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  transition={{ duration: 0.5, delay: node.delay }}
                  whileHover={{ scale: 1.05 }}
                >
                  <SystemNodeIcon>{node.icon}</SystemNodeIcon>
                  <SystemNodeTitle>{node.title}</SystemNodeTitle>
                  <SystemNodeDesc>{node.desc}</SystemNodeDesc>
                  {index < arr.length - 1 && (
                    <SystemNodeArrow>
                      {window.innerWidth <= 768 ? '↓' : '→'}
                    </SystemNodeArrow>
                  )}
                </SystemNode>
              ))}
          </SystemFlowContainer>
          
          <PrivateSystemNote>
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                Note: This represents the public recommendation flow. A more sophisticated analysis system 
                is maintained privately, incorporating advanced metrics and proprietary algorithms.
            </motion.div>
          </PrivateSystemNote>
        </Section>

        <Section 
          id="about"
          ref={aboutRef}
          as={motion.section}
          initial={{ opacity: 0, y: 50 }}
          animate={aboutInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <SectionTitle>About the Project</SectionTitle>
          <p>The AI Stock Analysis Platform is a sophisticated financial analysis tool that combines traditional technical analysis with advanced AI capabilities. Built as a passion project to bridge the gap between financial expertise and modern technology, it processes real-time market data and news to provide comprehensive investment insights.</p>

          <Features>
            <Feature>
              <FeatureIcon>📊</FeatureIcon>
              <FeatureTitle>Technical Analysis</FeatureTitle>
              <FeatureDesc>Advanced market indicators including SMA, EMA, MACD, RSI, and Bollinger Bands for trend identification and momentum analysis</FeatureDesc>
            </Feature>
            
            <Feature>
              <FeatureIcon>🧠</FeatureIcon>
              <FeatureTitle>Machine Learning</FeatureTitle>
              <FeatureDesc>Random Forest classifier trained on historical data to predict price movements with feature importance analysis</FeatureDesc>
            </Feature>
            
            <Feature>
              <FeatureIcon>📰</FeatureIcon>
              <FeatureTitle>Sentiment Analysis</FeatureTitle>
              <FeatureDesc>Natural Language Processing of financial news and social media data to gauge market sentiment and trends</FeatureDesc>
            </Feature>
            
            <Feature>
              <FeatureIcon>📱</FeatureIcon>
              <FeatureTitle>Automated Alerts</FeatureTitle>
              <FeatureDesc>Customizable email notifications for top stock picks and significant market movements</FeatureDesc>
            </Feature>
          </Features>

        </Section>

        <Section 
          id="tech-stack"
          ref={techStackRef}
          as={motion.section}
          initial={{ opacity: 0, y: 50 }}
          animate={techStackInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <SubsectionTitle>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={techStackInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Technology Stack
            </motion.div>
          </SubsectionTitle>
          
          <TechGrid>
            {[
              { name: 'Python Backend', desc: 'Core application server with Django REST framework', delay: 0.4 },
              { name: 'React + TypeScript', desc: 'Modern frontend with type safety', delay: 0.5 },
              { name: 'ML Pipeline', desc: 'TensorFlow and Scikit-learn for predictions', delay: 0.6 },
              { name: 'Docker + CI/CD', desc: 'Containerized deployment with automated testing', delay: 0.7 },
              { name: 'Financial APIs', desc: 'Real-time market data integration', delay: 0.8 },
              { name: 'NLP Models', desc: 'BERT-based sentiment analysis', delay: 0.9 },
              { name: 'PostgreSQL', desc: 'Scalable data storage and analytics', delay: 1.0 },
              { name: 'D3.js', desc: 'Interactive data visualizations', delay: 1.1 }
            ].map((tech, index) => (
              <TechItem 
                key={index}
                as={motion.div}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={techStackInView ? { opacity: 1, scale: 1 } : {}}
                transition={{ duration: 0.5, delay: tech.delay }}
              >
                <TechName>{tech.name}</TechName>
                <TechDescription>{tech.desc}</TechDescription>
              </TechItem>
            ))}
          </TechGrid>
        </Section>

        <ProjectLinks>
          <Button primary onClick={() => {
            navigate('/'); // First navigate to home
            // Wait for navigation to complete then scroll
            setTimeout(() => {
              scrollToSection('projects');
            }, 50);
          }}>Back to Projects</Button>
        </ProjectLinks>
      </PageContent>
    </ProjectLayout>
  );
};

// Don't export yet, we'll do it at the end of the file
// export default StockAnalysisPage;

// Styled Components
// Base Layout Components
const HeroSection = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  background: linear-gradient(135deg, #000000 0%, #001a00 50%, #003300 100%);
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
  
  h1 {
    font-size: 4rem;
    margin-bottom: 1rem;
    background: linear-gradient(to right, #FFFFFF, #22c55e);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
  }
  
  p {
    font-size: 1.5rem;
    opacity: 0.9;
    color: #a0aec0;
    
    @media (max-width: 768px) {
      font-size: 1.2rem;
    }
  }
`;

const ScrollDown = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  cursor: pointer;
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  transition: color 0.3s ease;
  z-index: 100;
  
  span {
    margin-bottom: ${props => props.theme.spacing.sm};
  }
  
  svg {
    animation: bounce 2s infinite;
  }
  
  &:hover {
    color: #22c55e;
  }
  
  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% {
      transform: translateY(0);
    }
    40% {
      transform: translateY(-10px);
    }
    60% {
      transform: translateY(-5px);
    }
  }

  @media (max-height: 600px) {
    display: none;
  }
`;

const PageContent = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  position: relative;
  z-index: 1;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    padding: 0 ${props => props.theme.spacing.md};
  }
`;

const Section = styled.section`
  padding: 3rem 0;
  width: 100%;
  position: relative;
  
  @media (max-width: 768px) {
    padding: 2rem 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  gap: 1rem;
  position: relative;
  padding-bottom: 1rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 3px;
    background: linear-gradient(to right, #22c55e, rgba(34, 197, 94, 0.3), transparent);
    border-radius: 2px;
  }
`;

// Stock Recommendation Components
const RecommendationBox = styled.div`
  background: rgba(34, 197, 94, 0.05);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(34, 197, 94, 0.1);
`;

const RecommendationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  span {
    font-size: 0.9rem;
    opacity: 0.7;
  }
`;

const TopPickSection = styled.div`
  padding: 1rem;
`;

const TopPickContainer = styled.div`
  background: rgba(34, 197, 94, 0.05);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(34, 197, 94, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(34, 197, 94, 0.1);
  }
`;

const TopPickMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const LastUpdated = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const TopPickMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RankIndicator = styled.div`
  color: white;
  font-weight: 700;
  font-size: 2rem;
`;

const BullishScoreIndicator = styled.div<{ score: number }>`
  background: ${props => {
    if (props.score >= 80) return 'rgba(74, 222, 128, 0.2)';
    if (props.score >= 60) return 'rgba(251, 191, 36, 0.2)';
    return 'rgba(248, 113, 113, 0.2)';
  }};
  color: ${props => {
    if (props.score >= 80) return '#4ade80';
    if (props.score >= 60) return '#fbbf24';
    return '#f87171';
  }};
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
`;

const StockInfo = styled.div`
  margin-bottom: 2rem;
`;

const StockTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const StockSymbol = styled.h3`
  font-size: 2rem;
  font-weight: 700;
  margin: 0;
`;

const StockName = styled.h4`
  font-size: 1.2rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const StockMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(5, minmax(0, max-content));
  gap: 4rem;
  justify-content: space-between;
  margin-bottom: 1.2rem;
  padding: 1.2rem 2rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
  
  &:last-of-type {
    margin-bottom: 2rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    gap: 3rem;
    padding: 1.2rem 1.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(3, minmax(0, max-content));
    gap: 2.5rem;
    justify-content: center;
    padding: 1.2rem 1rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, minmax(0, max-content));
    gap: 2rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: minmax(0, max-content);
    gap: 1.5rem;
    padding: 1.2rem 0.5rem;
  }
`;

const MetricItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
`;

const MetricLabel = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MetricValue = styled.span<{ change?: number }>`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${props => {
    if (props.change === undefined) return 'inherit';
    return props.change >= 0 ? '#4ade80' : '#f87171';
  }};
`;

const AnalysisSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 12px;
`;

const AnalysisTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

// System Flow Components
const SystemFlowTitle = styled.h3`
  font-size: 1.5rem;
  margin: 3rem 0 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
`;

const SystemFlowContainer = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 2rem;
  padding: 2rem 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: 3rem;
    align-items: center;
    padding: 1rem;
    overflow-x: visible;
  }
`;

const SystemNode = styled(motion.div)<SystemNodeProps>`
  position: relative;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(21, 128, 61, 0.2) 100%);
  padding: 1.5rem;
  border-radius: 12px;
  width: ${props => {
    if (props.id === 'server') return '220px';
    if (props.id === 'storage') return '220px';
    return '180px';
  }};
  height: 160px;
  text-align: center;
  border: 1px solid rgba(34, 197, 94, 0.2);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: ${props => {
      if (props.id === 'server') return '200px';
      if (props.id === 'storage') return '200px';
      return '160px';
    }};
    height: 140px;
    padding: 1.5rem 1.5rem 1.8rem;
    flex: 0 0 auto;
  }
`;

const SystemNodeIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 0.5rem;
`;

const SystemNodeTitle = styled.h4`
  font-size: 1rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const SystemNodeDesc = styled.p`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.4;
`;

const SystemNodeArrow = styled.div`
  position: absolute;
  right: -2rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.5rem;
  color: #22c55e;
  opacity: 0.8;
  pointer-events: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    right: 50%;
    top: 100%;
    transform: translateX(50%);
    transform-origin: center;
    content: '↓';
  }
`;

const PrivateSystemNote = styled.div`
  margin-top: 2rem;
  padding: 1rem;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 8px;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  border: 1px dashed rgba(34, 197, 94, 0.2);
`;

// Navigation Components
const ProjectLinks = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin: 4rem 0;
`;

const Button = styled.button<ButtonProps>`
  background: ${props => props.primary ? 
    'linear-gradient(90deg, #22c55e 0%, #16a34a 100%)' : 
    'rgba(255, 255, 255, 0.05)'
  };
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(34, 197, 94, 0.3);
  }
`;

const AnalysisText = styled.p`
  margin-top: 1rem;
  line-height: 1.6;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const LoadingText = styled.div`
  text-align: center;
  padding: 3rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Feature Section Components
const Features = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 2rem;
  margin: 3rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const Feature = styled.div`
  padding: 2rem;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.1);
`;

const FeatureIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 1rem;
`;

const FeatureTitle = styled.h4`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Tech Stack Components
const SubsectionTitle = styled.h3`
  font-size: 2rem;
  margin: 3rem 0 2rem;
  text-align: center;
  color: ${({ theme }) => theme.colors.text};
`;

const TechGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const TechItem = styled(motion.div)`
  padding: 1.5rem;
  background: rgba(34, 197, 94, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.1);
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const TechName = styled.h4`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.text};
`;

const TechDescription = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
`;

const DisclaimerText = styled.p`
  font-size: 0.8rem;
  opacity: 0.7;
  text-align: center;
  margin-top: 1.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// Top 10 Table Components
const TopTenHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 3rem 0 1.5rem;
  
  h3 {
    font-size: 1.5rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
  }
  
  span {
    font-size: 0.9rem;
    opacity: 0.7;
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const TopTenTableContainer = styled.div`
  background: rgba(34, 197, 94, 0.05);
  border-radius: 16px;
  padding: 1.5rem;
  border: 1px solid rgba(34, 197, 94, 0.1);
  overflow-x: auto;
  margin-bottom: 2rem;
  
  /* Smooth scrolling on mobile */
  -webkit-overflow-scrolling: touch;
  
  /* Scrollbar styling for webkit browsers */
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(34, 197, 94, 0.3);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(34, 197, 94, 0.5);
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const TopTenTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 1500px; /* Increased minimum width for the additional columns */
  
  th {
    padding: 1rem 0.6rem;
    text-align: left;
    font-weight: 600;
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.text};
    border-bottom: 2px solid rgba(34, 197, 94, 0.2);
    white-space: nowrap;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 0.75rem 0.45rem;
      font-size: 0.75rem;
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: 0.5rem 0.2rem;
      font-size: 0.65rem;
    }
  }
  
  td {
    padding: 1rem 0.6rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    white-space: nowrap;
    font-size: 0.9rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      padding: 0.75rem 0.45rem;
      font-size: 0.8rem;
    }
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding: 0.5rem 0.2rem;
      font-size: 0.7rem;
    }
  }
  
  /* Specific column widths */
  th:nth-child(1),
  td:nth-child(1) {
    width: 50px; /* Rank */
  }
  
  th:nth-child(2),
  td:nth-child(2) {
    width: 80px; /* Ticker */
  }
  
  th:nth-child(3),
  td:nth-child(3),
  th:nth-child(4),
  td:nth-child(4),
  th:nth-child(5),
  td:nth-child(5) {
    min-width: 70px; /* Score, Tier, Signal */
  }
  
  th:nth-child(6),
  td:nth-child(6),
  th:nth-child(7),
  td:nth-child(7) {
    min-width: 90px; /* Entry, Risk/Reward */
  }
`;

const TopTenRow = styled(motion.tr)`
  transition: background-color 0.2s ease;
  
  &:hover {
    background: rgba(34, 197, 94, 0.1);
  }
  
  .rank {
    font-weight: 600;
    color: ${({ theme }) => theme.colors.text};
    min-width: 60px;
  }
  
  .ticker {
    font-weight: 700;
    font-size: 1rem;
    color: ${({ theme }) => theme.colors.text};
    min-width: 80px;
  }
  
  .price {
    font-weight: 600;
    min-width: 80px;
  }
  
  .change {
    min-width: 90px;
    text-align: center;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      min-width: 75px;
    }
  }
  
  .rsi {
    text-align: center;
    min-width: 60px;
    font-size: 0.9rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 50px;
      font-size: 0.8rem;
    }
  }
  
  .macd {
    text-align: center;
    min-width: 80px;
    font-size: 0.85rem;
    text-transform: uppercase;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 65px;
      font-size: 0.7rem;
    }
  }
  
  .support {
    text-align: center;
    min-width: 85px;
    font-size: 0.9rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 70px;
      font-size: 0.8rem;
    }
  }
  
  .resistance {
    text-align: center;
    min-width: 85px;
    font-size: 0.9rem;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 70px;
      font-size: 0.8rem;
    }
  }
  
  .pattern {
    text-align: center;
    min-width: 70px;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 60px;
    }
  }
  
  .ml-accuracy {
    text-align: center;
    min-width: 80px;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 65px;
    }
  }
  
  .sentiment {
    text-align: center;
    min-width: 80px;
    
    @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
      min-width: 65px;
    }
  }
`;

const ScoreBadge = styled.div<{ score: number }>`
  background: ${props => {
    if (props.score >= 95) return 'rgba(74, 222, 128, 0.2)';
    if (props.score >= 85) return 'rgba(251, 191, 36, 0.2)';
    return 'rgba(248, 113, 113, 0.2)';
  }};
  color: ${props => {
    if (props.score >= 95) return '#4ade80';
    if (props.score >= 85) return '#fbbf24';
    return '#f87171';
  }};
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
  text-align: center;
  display: inline-block;
  min-width: 50px;
`;

const ChangeValue = styled.span<{ change: number }>`
  font-weight: 600;
  color: ${props => props.change >= 0 ? '#4ade80' : '#f87171'};
`;

const SentimentValue = styled.span<{ sentiment: number }>`
  font-weight: 600;
  color: ${props => {
    if (props.sentiment > 0.3) return '#4ade80';
    if (props.sentiment > 0) return '#fbbf24';
    if (props.sentiment > -0.3) return '#f97316';
    return '#f87171';
  }};
`;

const TopPickCard = styled.div`
  background: rgba(34, 197, 94, 0.05);
  border-radius: 12px;
  padding: 2rem;
  border: 1px solid rgba(34, 197, 94, 0.1);
  margin-bottom: 2rem;
`;

const TopPickHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const TopPickDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 6px;
`;

const DetailLabel = styled.span`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const DetailValue = styled.span<{ change?: number }>`
  font-weight: 500;
  color: ${props => {
    if (props.change === undefined) return 'inherit';
    return props.change >= 0 ? '#4ade80' : '#f87171';
  }};
`;

const BullishScore = styled.div<{ score?: number }>`
  background: ${props => {
    if (!props.score) return 'rgba(255, 255, 255, 0.1)';
    if (props.score >= 80) return 'rgba(74, 222, 128, 0.2)';
    if (props.score >= 60) return 'rgba(251, 191, 36, 0.2)';
    return 'rgba(248, 113, 113, 0.2)';
  }};
  color: ${props => {
    if (!props.score) return 'white';
    if (props.score >= 80) return '#4ade80';
    if (props.score >= 60) return '#fbbf24';
    return '#f87171';
  }};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 600;
`;

// MacdValue component for technical indicators
const MacdValue = styled.span<{ status: string }>`
  font-weight: 600;
  color: ${props => {
    const status = props.status.toLowerCase();
    if (status.includes('bullish')) return '#4ade80';
    if (status.includes('bearish')) return '#f87171';
    return 'inherit';
  }};
`;

// Market Selector Components
const MarketSelectorContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
`;

const MarketSelector = styled.div`
  display: flex;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const MarketOption = styled.button<{ active: boolean }>`
  padding: 1rem 2rem;
  background: ${props => props.active ? 'rgba(34, 197, 94, 0.2)' : 'transparent'};
  color: ${props => props.active ? '#22c55e' : 'inherit'};
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: ${props => props.active ? '600' : '400'};
  
  &:hover {
    background: rgba(34, 197, 94, 0.1);
    color: #22c55e;
  }
  
  @media (max-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
  }
`;

// Interface for tier styling
interface TierProps {
  tier: string;
}

// Tier styling component for quality tiers
const TierValue = styled.span<TierProps>`
  font-weight: 600;
  color: ${props => {
    const tier = props.tier.toUpperCase();
    if (tier === 'A') return '#10b981'; // Green for A tier
    if (tier === 'B') return '#4ade80'; // Light green for B tier
    if (tier === 'C') return '#fbbf24'; // Yellow for C tier
    if (tier === 'D') return '#f87171'; // Red for D tier
    return 'inherit';
  }};
`;

// Interface for regime styling
interface RegimeProps {
  regime: string;
}

// Regime styling component for market regimes
const RegimeValue = styled.span<RegimeProps>`
  font-weight: 600;
  color: ${props => {
    const regime = props.regime.toLowerCase();
    if (regime.includes('bull')) return '#4ade80'; // Green for bullish
    if (regime.includes('bear')) return '#f87171'; // Red for bearish
    if (regime.includes('neutral')) return '#94a3b8'; // Gray for neutral
    if (regime.includes('volatile')) return '#fbbf24'; // Yellow for volatile
    return 'inherit';
  }};
`;

// Signal styling component for long/momentum signals
const SignalValue = styled.span<SignalProps>`
  font-weight: 600;
  color: ${props => {
    const signal = props.signal.toLowerCase();
    if (signal.includes('momentum')) return '#fbbf24'; // Yellow for momentum
    if (signal.includes('long')) return '#4ade80'; // Green for long
    if (signal.includes('short')) return '#f87171'; // Red for short
    return 'inherit';
  }};
`;

// Data Guide Components
const DataGuideSection = styled.div`
  margin: 2rem 0;
  background: rgba(34, 197, 94, 0.03);
  border-radius: 12px;
  border: 1px solid rgba(34, 197, 94, 0.1);
  overflow: hidden;
`;

const DataGuideToggle = styled.button<{ expanded: boolean }>`
  width: 100%;
  padding: 1rem 1.5rem;
  background: ${props => props.expanded ? 'rgba(34, 197, 94, 0.05)' : 'transparent'};
  border: none;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(34, 197, 94, 0.05);
  }
  
  span {
    font-size: 1rem;
  }
`;

const DataGuideChevron = styled.span<{ expanded: boolean }>`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  transform: ${props => props.expanded ? 'rotate(0deg)' : 'rotate(0deg)'};
  transition: transform 0.3s ease;
`;

const DataGuideContent = styled.div`
  padding: 0 1.5rem 1.5rem;
  overflow: hidden;
`;

const DataGuideGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const DataGuideCategory = styled.div`
  background: rgba(255, 255, 255, 0.02);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
  height: fit-content;
`;

const DataGuideCategoryTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const DataGuideItem = styled.p`
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  strong {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 600;
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DataGuideNote = styled.div`
  background: rgba(34, 197, 94, 0.05);
  padding: 1rem;
  border-radius: 8px;
  border-left: 3px solid #22c55e;
  font-size: 0.85rem;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.textSecondary};
  
  strong {
    color: ${({ theme }) => theme.colors.text};
  }
`;

export default StockAnalysisPage;